
  import { Vue, Component, Prop } from "vue-property-decorator";
  import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
  import CardAvatar from "@/components/card/CardAvatar.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import { Tag, User } from "@/generated/graphql";
  import TagMultiselect from "@/components/autocompleteMultiselects/TagMultiselect.vue";
  import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
  import ADD_CONTRACTOR_TAG from "@/apollo/tag/AddContractorTag.gql"
  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import CardImage from "@/components/common/CardImage.vue";

  interface ModalContent {
    title: string,
    actionButtonText: string,
  }

  interface UpdateContractorTagArgs {
    tagId: string | null,
    contractorId: string,
  }

  @Component({
    components: {
      CardImage,
      ModalSectionsDivider,
      TagMultiselect,
      UiButton,
      CardAvatar,
      ModalFullScreen,
    },
  })
  export default class EmployeeTagModal extends Vue {
    @Prop({ type: String, required: true }) readonly fullName!: string;
    @Prop({ type: String, required: true }) readonly initials!: string;
    @Prop({ type: String, required: true }) readonly userUpdatedDate!: string;
    @Prop({ type: Boolean, default: false }) readonly available!: boolean;
    @Prop({ type: Object, default: undefined }) readonly appliedTag?: Tag;
    @Prop({ type: String, default: undefined }) readonly avatar?: string;
    @Prop({ type: String, default: undefined }) readonly contractorId?: string;

    disabled: boolean = false;
    processing: boolean = false;
    selectedTagId: string | null = null;
    user: User | null = null

    get appliedTagId(): string | undefined {
      return this.appliedTag?.id;
    }

    get modalData(): ModalContent {
      if (this.appliedTag) {
        return {
          title: "Update employee tag",
          actionButtonText: "Update",
        };
      }

      return {
        title: "Add employee tag",
        actionButtonText: "Add",
      };
    }

    updateEmployeeTag(): Promise<void> | void {
      const contractorId = this.contractorId;

      if (contractorId) {
        return this.displayUserAction(
          () => this.updateContractorTag({
            tagId: this.selectedTagId,
            contractorId,
          }),
          this.appliedTagId
        );
      }

      throw new Error();
    }

    setTagId(tagId: string): void {
      this.selectedTagId = tagId.length === 0 ? null : tagId;
    }

    async updateContractorTag({ tagId, contractorId }: UpdateContractorTagArgs): Promise<void> {
      await this.$apollo.mutate({
        mutation: ADD_CONTRACTOR_TAG,
        variables: {
          tagId,
          contractorId,
        },
      });
    }

    async displayUserAction(action: () => Promise<void>, tagId?: string): Promise<void> {
      if (this.processing) {
        return;
      }

      this.processing = true;

      try {
        await action();

        this.$emit("tagUpdated");

        if (tagId) {
          this.$eventBus.emit(NOTIFICATION, Message.successMessage("Tag successfully updated"));
        } else {
          this.$eventBus.emit(NOTIFICATION, Message.successMessage("Tag successfully added"));
        }

      } catch (e) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Something went wrong!"));
      } finally {
        this.processing = false;
      }
    }
  }
